import { Pill } from "@/components/ui/pill";
import { Button } from "@/components/ui/button";
import type { SequenceRankingRow } from "@/types/sequence.types";

interface Props {
  rows: SequenceRankingRow[];
  metricLabel: string;
  selectedId: string | null;
  onSelect: (simulationId: string) => void;
}

const num = (v: number | null, unit: string) => (v == null ? "—" : `${v} ${unit}`);

/** Delta contra a fila atual. Menor é melhor nos três objetivos, então negativo é ganho. */
function DeltaPill({ delta }: { delta: number | null }) {
  if (delta == null) return <Pill tone="neutral">sem comparação</Pill>;
  if (delta < 0) return <Pill tone="success" withDot>{`${delta} vs. hoje`}</Pill>;
  if (delta > 0) return <Pill tone="danger" withDot>{`+${delta} vs. hoje`}</Pill>;
  return (
    <Pill tone="neutral" withDot>
      igual a hoje
    </Pill>
  );
}

// Ranking dos candidatos. A fila atual aparece SEMPRE (é a referência): sem ela o usuário não
// tem como saber se a "melhor sequência" é melhor do que já fazer.
export function SequenceRanking({ rows, metricLabel, selectedId, onSelect }: Props) {
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li
          key={row.simulationId}
          className={
            row.simulationId === selectedId
              ? "rounded-md border border-primary bg-muted/30 p-3"
              : "rounded-md border p-3"
          }
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{row.label}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {row.isBaseline ? <Pill tone="neutral">fila atual</Pill> : null}
                {row.isWinner ? (
                  <Pill tone="success" withDot>
                    recomendada
                  </Pill>
                ) : null}
                {!row.completed && row.status === "COMPLETED" ? (
                  <Pill tone="warning" withDot>
                    não cabe no horizonte
                  </Pill>
                ) : null}
                {row.status === "FAILED" ? (
                  <Pill tone="danger" withDot>
                    falhou
                  </Pill>
                ) : null}
                {!row.isBaseline ? <DeltaPill delta={row.deltaObjective} /> : null}
              </div>
            </div>
            <Button
              size="sm"
              variant={row.simulationId === selectedId ? "default" : "outline"}
              disabled={!row.completed}
              onClick={() => onSelect(row.simulationId)}
            >
              Ver ordens
            </Button>
          </div>

          <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">Atraso total</dt>
              <dd className="font-medium tabular-nums">{num(row.latenessMin, "min")}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Setup total</dt>
              <dd className="font-medium tabular-nums">{num(row.setupMin, "min")}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duração da fila</dt>
              <dd className="font-medium tabular-nums">{num(row.makespanH, "h")}</dd>
            </div>
          </dl>
          <p className="mt-1 text-[10px] text-muted-foreground">Objetivo: {metricLabel}</p>
        </li>
      ))}
    </ul>
  );
}
